import { useUser } from '@myosh/myosh-login';
import type { DateFilterRangeType } from 'apps/dashboard/src/redux/config/filters-api';
import { useCallback, useEffect, useState } from 'react';
import { convertToGlobalFilterDetails } from '../../../context/globalDateRange.context';
import { getDashboardFiltersCombination } from '../../../helpers/dashboard-filter-formatter';
import { getStorageItem } from '../../../helpers/storage';
import { useConfiguredDateRanges } from '../../../hooks/useConfiguredDateRanges';
import { useDefaultDateRange } from '../../../hooks/useDefaultDateRange';
import { useCreateViewMutation, useGetAllViewsQuery, useLazyGetViewQuery } from '../../../redux/config/views';
import { useAppDispatch } from '../../../redux/hooks';
import { removeGlobalDateFilters } from '../../../redux/slices/globalFilter/global-dates-filter-slice';
import { setSelectedView } from '../../../redux/slices/selected-view';
import { useSelectedView } from '../../Charts/hooks/use-selected-view';
import { customDateRangeFlag, quickDateRange } from '../utilities';
import type { ViewNameOption } from '../view-components/view-dropdown';

function useSyncView() {
  const dispatch = useAppDispatch();
  const dateRanges = useConfiguredDateRanges();
  const defaultDateRange = useDefaultDateRange();
  const user = useUser();

  const { data: views, isFetching } = useGetAllViewsQuery();
  const [getView] = useLazyGetViewQuery();
  const [addView] = useCreateViewMutation();

  const currentView = useSelectedView();
  const [transformedViews, setTransformedViews] = useState<Array<ViewNameOption>>([]);

  useEffect(() => {
    if (views && !isFetching) {
      const viewOptions = views.map((view) => {
        return {
          text: view.name,
          value: view.id,
          defaultView: view.defaultView,
          ...view,
        };
      });
      setTransformedViews(viewOptions);
    }
  }, [views, isFetching]);

  const updateSelectedView = useCallback(
    (viewId?: string) => {
      const view = transformedViews?.find((view) => view.id === viewId);
      if (view) {
        getView(view.id)
          .unwrap()
          .then((result) => {
            dispatch(removeGlobalDateFilters());
            const predefinedRange = result?.dateFiltersForView
              ? dateRanges?.find((range) => range.id === result.dateFiltersForView?.dateFilterRangeType)
              : //if no date filters are supplied, use explicitly the default ones
                defaultDateRange;

            let viewPayload;
            if (predefinedRange) {
              const convertedDate = convertToGlobalFilterDetails(predefinedRange);
              viewPayload = {
                ...result,
                quickDateRange: convertedDate?.quickDateRange,
                dateFiltersForView: {
                  fromDate: convertedDate?.dateStart,
                  toDate: convertedDate?.dateEnd,
                  dateFilterRangeType: predefinedRange.id,
                },
              };
            } else {
              viewPayload = {
                ...result,
                quickDateRange: quickDateRange,
                dateFiltersForView: {
                  ...result.dateFiltersForView,
                  dateFilterRangeType: customDateRangeFlag,
                },
              };
            }

            dispatch(setSelectedView(viewPayload));
          });
      }
    },
    [transformedViews, dateRanges, defaultDateRange, dispatch, getView]
  );

  useEffect(() => {
    if (views) {
      const lastlyUsedView = getStorageItem('lastUsedView', 'sessionStorage');
      const lastUsedViewId = lastlyUsedView?.id;

      if (!currentView?.id) {
        if (lastUsedViewId) {
          updateSelectedView(lastUsedViewId);
        } else {
          const defaultView = transformedViews.find((view) => view.defaultView);
          if (defaultView) {
            updateSelectedView(defaultView.id);
          }
        }
      }
    }
  }, [transformedViews, updateSelectedView, currentView]);

  /** Adds a default view for a user, if there are none exist in its views. */
  useEffect(() => {
    if (
      !isFetching &&
      defaultDateRange &&
      (views?.length === 0 || (views && views.find((view) => view.defaultView) === undefined))
    ) {
      const dates = convertToGlobalFilterDetails(defaultDateRange);
      addView({
        name: 'Default',
        privateView: false,
        defaultView: true,
        userId: user.state.user?.preferred_username ?? '',
        dashboardFiltersCombination: getDashboardFiltersCombination({
          dateFilterRangeType: dates?.quickDateRange.value as DateFilterRangeType,
        }),
      });
    }
  }, [views, isFetching, defaultDateRange, addView, user.state.user?.preferred_username]);

  return {
    updateView: updateSelectedView,
    viewOptions: transformedViews,
    isFetchingViews: isFetching,
  };
}

export default useSyncView;
